// Angular
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
// RxJS
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil, tap } from 'rxjs/operators';
// Store


// Auth
import { AuthNoticeService, AuthService } from '../../../../core/auth';
import { ToastrService } from 'ngx-toastr';
import { Title } from '@angular/platform-browser';
import { environment } from '../../../../../environments/environment';

@Component({
	selector: 'kt-login',
	templateUrl: './login.component.html',
	encapsulation: ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.Default
})
export class LoginComponent implements OnInit, OnDestroy {
	// Public params
	loginForm: FormGroup;
	loading = false;
	isLoggedIn$: Observable<boolean>;
	errors: any = [];
	userData: any = [];
	private unsubscribe: Subject<any>;

	private returnUrl: any;
	status: any;
	is_submit_enabled: boolean = true;
	// Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

	/**
	 * Component constructor
	 *
	 * @param router: Router
	 * @param auth: AuthService
	 * @param authNoticeService: AuthNoticeService
	 * @param translate: TranslateService
	 * @param store: Store<AppState>
	 * @param fb: FormBuilder
	 * @param cdr
	 * @param route
	 */
	constructor(
		private router: Router,
		private auth: AuthService,
		private authNoticeService: AuthNoticeService,
		private fb: FormBuilder,
		private cdr: ChangeDetectorRef,
		private route: ActivatedRoute,
		private toastr: ToastrService,
		private titleService: Title
	) {
		this.unsubscribe = new Subject();
		this.titleService.setTitle('Debtor Education | Login');
	}

	/**
	 * @ Lifecycle sequences => https://angular.io/guide/lifecycle-hooks
	 */

	/**
	 * On init
	 */
	ngOnInit(): void {
		this.initLoginForm();

		// redirect back to the returnUrl before login
		this.route.queryParams.subscribe(params => {
			this.returnUrl = params['returnUrl'] || '/dashboard';
		});
		if (localStorage.getItem('currentUser')) {
			this.router.navigateByUrl('/dashboard');
		}
	}

	/**
	 * On destroy
	 */
	ngOnDestroy(): void {
		this.authNoticeService.setNotice(null);
		this.unsubscribe.next();
		this.unsubscribe.complete();
		this.loading = false;
	}

	/**
	 * Form initalization
	 * Default params, validators
	 */
	initLoginForm() {
		// demo message to show


		this.loginForm = this.fb.group({
			email: ['', Validators.compose([
				Validators.required,
				Validators.email,
				Validators.minLength(3),
				Validators.maxLength(320) // https://stackoverflow.com/questions/386294/what-is-the-maximum-length-of-a-valid-email-address
			])
			],
			password: ['', Validators.compose([
				Validators.required,
				Validators.minLength(3),
				Validators.maxLength(100)
			])
			]
		},
		{ updateOn: "blur" });
	}

	/**
	 * Form Submit
	 */
	submit() {
		this.is_submit_enabled = true;
		const controls = this.loginForm.controls;
		/** check form */
		if (this.loginForm.invalid) {
			Object.keys(controls).forEach(controlName =>
				controls[controlName].markAsTouched()
			);
			return;
		}
		this.loading = true;

		const authData = {
			email: controls['email'].value,
			password: controls['password'].value
		};
		this.is_submit_enabled = false;
		this.auth
			.login(authData.email, authData.password)
			.pipe(
				tap(user => {
					this.userData = user;
					if (this.userData.data.jwToken && this.userData.data.roles[0]) {

						if(this.userData.data.roles[0]){

							if(this.userData.data.roles[0]=="Admin"){
								this.userData.data.userType = 1
							}else if(this.userData.data.roles[0]=="Employee"){
								this.userData.data.userType =2
							}
						}
						if (this.userData.data.userType == 1) {
							
							localStorage.setItem('authToken', this.userData.data.jwToken);						
							localStorage.setItem(btoa('userrole'), btoa(environment.admin));
							localStorage.setItem(btoa('userrolekey'), btoa(environment.userrolekeyadmin));
							localStorage.setItem(environment.authTokenKey, this.userData.data.jwToken);
							localStorage.setItem('currentUser', JSON.stringify(this.userData.data));							
						} else if (this.userData.data.userType == 2){
							localStorage.setItem('authToken', this.userData.data.jwToken);						
							localStorage.setItem(btoa('userrole'), btoa(environment.employee));
							localStorage.setItem(btoa('userrolekey'), btoa(environment.userrolekeyemployee));
							localStorage.setItem(environment.authTokenKey, this.userData.data.jwToken);
							localStorage.setItem('currentUser', JSON.stringify(this.userData.data));																			
						} 
						if (localStorage.getItem('path') && localStorage.getItem('path') != '/') {
							this.router.navigateByUrl(localStorage.getItem('path')).then(()=> { 															
								this.toastr.success('Successfully Login', 'Success!');
								this.loading = false;
								this.cdr.markForCheck();	
								this.cdr.detectChanges();							
							}) 	
						} else {
							this.router.navigateByUrl(this.returnUrl).then(()=> { 							
								this.toastr.success('Successfully Login', 'Success!');
								this.loading = false;
								this.cdr.markForCheck();	
								this.cdr.detectChanges();							
							}) 																										
						}
						if (localStorage.getItem('path')) {
							localStorage.removeItem('path');
						}
					}
				}),
				takeUntil(this.unsubscribe),
				finalize(() => {					
					this.cdr.markForCheck();
					this.cdr.detectChanges();
				})
			)
			.subscribe(()=>{}, err => { 
				if (err) {
					this.router.navigateByUrl(this.returnUrl).then(()=> { 							
						this.is_submit_enabled = true;
						this.toastr.error('Invalid Credentials', 'Error!');
						this.loading = false;
						this.cdr.detectChanges();
					}) 						
				}
			});
	}
	/**
	 * Checking control validation
	 *
	 * @param controlName: string => Equals to formControlName
	 * @param validationType: string => Equals to valitors name
	 */
	isControlHasError(controlName: string, validationType: string): boolean {
		const control = this.loginForm.controls[controlName];
		if (!control) {
			return false;
		}
		const result = control.hasError(validationType) && (control.dirty || control.touched);
		return result;
	}
}
